/* @jsx h */
import { useMenu } from "@app/vue/providers/index";
import { calculateCartItemTotal, formatPrice } from "@lemmonhq/orders";
import { CartItem } from "@lemmonhq/types";
import { computed, defineComponent, h, ref } from "vue";
import { RouterLink } from "vue-router";

import { LemmonModal, useLocalization } from "@lemmonhq/uikit";

export const CartItemNoteModal = defineComponent<{
  onClose: Function;
  onSuccess: (id: string, note?: string) => void;
  cartItem: CartItem;
}>({
  props: ["onClose", "onSuccess", "cartItem"],
  setup(props) {
    const note = ref(props.cartItem.note || "");
    const { t } = useLocalization();

    return () => {
      return (
        <LemmonModal
          title={t("customer.checkout.add_note")}
          onClose={props.onClose}
          renderButtons={() => {
            return (
              <div class="p-4 border-t border-gray-700">
                <div class="flex justify-between gap-2">
                  <button
                    onClick={() => props.onClose()}
                    class="btn btn-secondary"
                  >
                    {t("customer.checkout.cancel")}
                  </button>
                  <button
                    onClick={() => {
                      props.onSuccess(props.cartItem.guid, note.value);
                    }}
                    class="btn btn-primary"
                  >
                    {t("customer.checkout.save")}
                  </button>
                </div>
              </div>
            );
          }}
        >
          <div class="p-4">
            <label class="form-control w-full ">
              <div class="label hidden">
                <span class="label-text font-semibold">
                  {t("customer.checkout.notes")}
                </span>
              </div>
              <textarea
                value={note.value || ""}
                onInput={(e) => {
                  note.value = (e.target as HTMLTextAreaElement).value;
                }}
                class="input input-bordered w-full p-2"
                rows={2}
                placeholder={t("customer.checkout.notes_placeholder")}
              />
            </label>
          </div>
        </LemmonModal>
      );
    };
  },
});

//
const CartItemQuantitySelector = defineComponent<{
  item: CartItem;
  onIncrement: (guid: string) => void;
  onDecrement: (guid: string) => void;
}>({
  props: ["item", "onIncrement", "onDecrement"],
  setup(props) {
    return () => (
      <div class="flex items-center gap-2 text-sm">
        <button
          onClick={() => props.onDecrement(props.item.guid)}
          class="p-0 btn btn-sm btn-secondary w-7 h-7 min-h-0 flex justify-center items-center"
        >
          <span class="material-symbols-outlined text-md">remove</span>
        </button>
        <span class="w-7 h-7 bg-white text-black text-center border rounded flex justify-center items-center">
          {props.item.quantity}
        </span>
        <button
          onClick={() => props.onIncrement(props.item.guid)}
          class="p-0 btn btn-sm btn-secondary w-7 h-7 min-h-0 flex justify-center items-center"
        >
          <span class="material-symbols-outlined text-sm">add</span>
        </button>
      </div>
    );
  },
});

export const CartItems = defineComponent({
  setup() {
    const { cart, shop, groupOrder } = useMenu();
    const { t } = useLocalization();
    const showNotesModal = ref<CartItem>();
    const currency = computed(() => shop.value?.currency || "CHF");

    const updateCartItemNote = (guid: string, note?: string) => {
      cart.cart.items = cart.cart.items.map((item) =>
        item.guid === guid ? { ...item, note: note || "" } : item,
      );
    };

    return () => (
      <div class="p-2 ">
        <h3 class="text-lg  mb-4 font-semibold">
          {t("customer.checkout.your_order")}{" "}
          {groupOrder.value?.table?.name
            ? `| ${groupOrder.value?.table.name}`
            : ""}
        </h3>
        <div class="space-y-3">
          {cart.cart.items.map((item) => (
            <div
              key={item.guid}
              class="flex justify-between items-center text-sm"
            >
              <div class="flex items-center gap-3 flex-1">
                <div class="">
                  <div class="flex items-center gap-2">
                    <button
                      onClick={() => (showNotesModal.value = item)}
                      class="p-0 hover:bg-gray-100 rounded-md transition-colors duration-200 self-start"
                      title={t("customer.checkout.add_note")}
                    >
                      <span class="material-symbols-outlined text-gray-500 text-2xl self-start">
                        {item.note ? "edit_note" : "edit_note"}
                      </span>
                    </button>
                    <span class="font-medium self-start">{item.name}</span>
                  </div>
                  {item.selectedBundles && (
                    <div class="pl-0">
                      {(item.selectedBundles || []).map(
                        (bundle, bundleIndex) => (
                          <div key={bundleIndex} class="text-[11px]">
                            <ul class="list-disc pl-5">
                              {bundle.options.map((option, optionIndex) => (
                                <li key={optionIndex}>
                                  {option.title}
                                  {option.price > 0 &&
                                    ` (+${formatPrice(option.price, false, 2, currency.value)})`}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                  {item.note && (
                    <p class="text-sm pl-0">
                      <span class="underline">
                        {t("customer.checkout.note_label")}
                      </span>{" "}
                      <span class="italic">{item.note}</span>
                    </p>
                  )}
                </div>
              </div>
              <div class="flex items-center gap-3 self-start">
                <div class="font-medium min-w-[60px] text-right self-start">
                  {item.hasPromotions ? (
                    <div class="flex items-center gap-2">
                      <span class="line-through  text-base-content/50">
                        {formatPrice(
                          item.maxRetailPrice,
                          false,
                          2,
                          currency.value,
                        )}
                      </span>
                      <span class="font-semibold text-success">
                        {formatPrice(item.fullPrice, false, 2, currency.value)}
                      </span>
                    </div>
                  ) : (
                    formatPrice(
                      calculateCartItemTotal(item),
                      false,
                      2,
                      currency.value,
                    )
                  )}
                </div>
                <CartItemQuantitySelector
                  item={item}
                  onIncrement={cart.incrementQuantity}
                  onDecrement={cart.decrementQuantity}
                />
              </div>
            </div>
          ))}
        </div>
        <div class="py-4">
          <RouterLink
            class="flex justify-center items-center py-1 btn btn-secondary"
            to={
              groupOrder.value
                ? `/restaurant/${shop.value.slug}/group-order/${groupOrder.value.id}`
                : "/"
            }
          >
            <span class="material-symbols-outlined text-2xl">add</span>{" "}
            {t("customer.checkout.back_to_menu")}
            <span class="text-2xl">🍋</span>
          </RouterLink>
        </div>
        {showNotesModal.value ? (
          <CartItemNoteModal
            cartItem={showNotesModal.value}
            onClose={() => {
              showNotesModal.value = undefined;
            }}
            onSuccess={(guid: string, note?: string) => {
              updateCartItemNote(guid, note);
              showNotesModal.value = undefined;
            }}
          />
        ) : null}
      </div>
    );
  },
});
